import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Trophy, ShieldStar } from "@phosphor-icons/react";
import { shopHref } from "@/lib/kmsAccess";

const STEPS = [
  "Sign up for the challenge.",
  "Choose who you are moving for.",
  "Join by purchasing Sweat for Sacrifice merch or making a minimum $20 entry contribution. The proceeds from the challenge will go to support Tunnel to Towers and FDNY Fire Family Transport.",
  "Log your minutes on the Sweat With Slay site all September.",
  "Share a name, photo, or story for the Legacy Wall (optional).",
  "Complete 911 total minutes by September 30.",
];

const PACES = [
  { lead: "30 min", sub: "every day" },
  { lead: "45–50 min", sub: "5 days a week" },
  { lead: "75 min", sub: "4 days a week" },
  { lead: "100 min", sub: "3 days a week" },
];

const PRIZES = [
  { name: "911 Finisher", line: "Everyone who completes 911 minutes earns 10% off your first month of any Sweat With Slay program — plus your name goes on the Legacy Wall under \u201cMoved with Purpose.\u201d" },
  { name: "Top Mover", line: "Most total minutes completed — receives a piece of Legacy merch + a highlight on the site and Instagram." },
  { name: "Community Spirit", line: "Highest donation OR most referrals brought into the challenge — receives a piece of Legacy merch." },
];

/**
 * The huge "Two ways to join" section from the Sacrifice page. Extracted out
 * of Sacrifice.jsx (was ~210 lines inline). Takes tier / setTier / merchPreview
 * as props so it can drive the live merch preview strip and the "Donate direct"
 * shortcut that flips the sign-up form tier to `custom`.
 */
export default function SacrificeTwoWaysToJoin({ tier, setTier, merchPreview }) {
  return (
    <section data-testid="sacrifice-two-ways" className="px-4 md:px-8 py-16 md:py-20 max-w-6xl mx-auto border-t border-white/10">
      <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
        {/* ===== LEFT — Participation side ===== */}
        <div className="space-y-14">
          {/* 6 simple steps */}
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Six simple steps</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl mb-8">How the Legacy<br /><span className="text-[#B32027]">Challenge works.</span></h2>
            <ol className="space-y-4">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-start gap-4">
                  <span className="font-display font-black text-[#B32027] text-2xl tracking-tighter shrink-0 w-8">0{i + 1}</span>
                  <span className="text-white/85 text-sm leading-snug pt-1">{s}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* The Challenge */}
          <div className="pt-10 border-t border-white/10">
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ The challenge</p>
            <h3 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-4xl mb-4">911 minutes.<br /><span className="text-[#B32027]">One month. Any way you move.</span></h3>
            <p className="text-white/80 text-sm md:text-base leading-relaxed mb-2">
              Complete 911 minutes of intentional movement in September — just over 30 min/day, however works for you.
            </p>
            <p className="font-serif italic text-base md:text-lg text-white mb-6">If you are moving with intention, it counts.</p>
            <div className="space-y-6">
              <div>
                <p className="text-white/55 uppercase tracking-[0.4em] text-[10px] font-bold mb-2">/ Movement counts</p>
                <ul className="grid grid-cols-2 gap-2 text-white/85">
                  {["Walking", "Lifting", "Running", "Cycling", "Yoga", "Stretching", "Swimming", "Golf", "Pickleball", "Dancing", "Classes", "Recovery"].map((m) => (
                    <li key={m} className="border border-white/15 px-3 py-2 text-sm"><Flame size={12} weight="fill" className="text-[#B32027] inline mr-2" />{m}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-white/55 uppercase tracking-[0.4em] text-[10px] font-bold mb-2">/ Pick your pace</p>
                <div className="grid grid-cols-2 gap-2">
                  {PACES.map((p) => (
                    <div key={p.lead} className="border border-white/15 bg-[#0F0F0F] p-3">
                      <p className="font-display font-black uppercase tracking-tighter text-lg">{p.lead}</p>
                      <p className="text-white/55 text-[10px] uppercase tracking-[0.2em] font-bold mt-1">{p.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Recognition/Prizes moved OUT of the left column — see full-width lateral row below */}
        </div>

        {/* ===== RIGHT — Money side ===== */}
        <div className="space-y-14">
          {/* Consolidated "Two ways to join" — single block that names the charities,
              explains the 50/50 split (donations vs merch), and points every visitor
              at the signup form below. No shortcut CTAs — everyone fills the form. */}
          <div id="transparency" data-testid="transparency-section">
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Two ways to join</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl mb-6">Buy or donate.<br /><span className="text-[#B32027]">Both give back.</span></h2>
            <p className="text-white/80 text-base leading-relaxed mb-6">
              <span className="text-white font-semibold">Sept 1–30, 2026</span>
            </p>

            <div className="border-2 border-[#B32027] bg-[#B32027]/10 p-5 md:p-6">
              <p className="text-white text-base leading-relaxed mb-4">
                Every entry supports the same two foundations — <strong className="text-white">Tunnel to Towers</strong> and the <strong className="text-white">FDNY Fire Family Transport Foundation</strong>.
              </p>
              <p className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl leading-tight mb-3">
                Every dollar splits <span className="text-[#B32027]">50/50</span>.
              </p>
              <p className="text-white text-sm md:text-base leading-relaxed">
                <strong>Donations:</strong> 100% goes through Pledge — Sweat With Slay never holds the funds.<br />
                <strong>Merch:</strong> net proceeds after production + shipping → same 50/50 split.
              </p>
              <p className="text-white/60 italic text-[11px] mt-4 pt-3 border-t border-white/20">
                Merch is a purchase, not tax-deductible. Sweat With Slay isn&rsquo;t affiliated with either foundation. Questions? <a href="mailto:Admin@sweatwithslay.com" className="underline text-white hover:text-[#B32027]">Admin@sweatwithslay.com</a>.
              </p>
            </div>

            <p className="text-white/75 text-sm leading-relaxed mt-6">
              To lock in your spot, fill out the signup form below — you&rsquo;ll pick your entry option there.
            </p>
          </div>

          {/* Foundations — moved UP under the 50/50 split per user request */}
          <div className="pt-10 border-t border-white/10">
            <p className="text-white/55 uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Where the proceeds go</p>
            <div className="space-y-3">
              <a href="https://t2t.org" target="_blank" rel="noreferrer noopener" data-testid="cause-t2t" className="group block border border-white/15 hover:border-[#B32027] bg-[#0F0F0F] p-5 transition-colors">
                <ShieldStar size={22} weight="fill" className="text-[#B32027] mb-3" />
                <p className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl">Tunnel to Towers</p>
                <p className="text-white/65 text-sm mt-2 leading-snug">Builds smart homes for catastrophically injured veterans &amp; first responders. Pays off mortgages for fallen heroes&rsquo; families.</p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 group-hover:text-[#B32027] flex items-center gap-2">t2t.org <ArrowUpRight size={11} weight="bold" /></p>
              </a>
              <a href="https://www.firefamilytransport.org/" target="_blank" rel="noreferrer noopener" data-testid="cause-fdny" className="group block border border-white/15 hover:border-[#B32027] bg-[#0F0F0F] p-5 transition-colors">
                <ShieldStar size={22} weight="fill" className="text-[#B32027] mb-3" />
                <p className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl">FDNY Fire Family Transport</p>
                <p className="text-white/65 text-sm mt-2 leading-snug">All-volunteer non-profit that drove Uncle Timmy to Sloan Kettering — and drives FDNY families to medical treatment every day.</p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 group-hover:text-[#B32027] flex items-center gap-2">firefamilytransport.org <ArrowUpRight size={11} weight="bold" /></p>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ===== FULL-WIDTH LATERAL ROW — Bonus + Recognition side-by-side ===== */}
      <div data-testid="bonus-recognition-row" className="mt-16 md:mt-20 pt-12 border-t border-white/10 grid md:grid-cols-2 gap-6 md:gap-10">
        {/* Bonus for participants */}
        <div className="border-2 border-white/15 bg-[#0F0F0F] p-5 md:p-6">
          <p className="text-[#B32027] uppercase tracking-[0.3em] text-[10px] font-bold mb-3">/ Bonus for participants</p>
          <h3 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-2xl md:text-3xl mb-4">Perks for<br /><span className="text-[#B32027]">signing up.</span></h3>
          <ul className="space-y-3 text-white text-sm md:text-base leading-relaxed">
            <li><strong className="text-[#B32027]">10% off your first month</strong> of any Sweat With Slay program when you donate or buy merch + complete the challenge.</li>
            <li><strong className="text-[#B32027]">Existing clients:</strong> $20 off next month.</li>
            <li><strong className="text-[#B32027]">Card with your merch?</strong> 10% off any session.</li>
          </ul>
        </div>

        {/* Recognition / Prizes — moved out of the left column into this lateral row */}
        <div className="border-2 border-white/15 bg-[#0F0F0F] p-5 md:p-6">
          <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-3">/ Recognition</p>
          <h3 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-2xl md:text-3xl mb-3">Not just<br /><span className="text-[#B32027]">most minutes.</span></h3>
          <p className="text-white/75 text-sm leading-relaxed mb-4">This challenge recognizes meaning, consistency, and community — not only the person with the most minutes.</p>
          <div className="space-y-3">
            {PRIZES.map((p) => (
              <div key={p.name} className="border border-white/15 bg-[#0A0A0A] p-3 flex items-start gap-3">
                <Trophy size={16} weight="fill" className="text-[#B32027] mt-0.5 shrink-0" />
                <div>
                  <p className="font-display font-black uppercase tracking-tighter text-sm">{p.name}</p>
                  <p className="text-white/65 text-xs leading-snug mt-1">{p.line}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-white/55 text-[11px] italic mt-4">Final prize details will be shared before September. Prizes awarded at Sweat With Slay&rsquo;s discretion.</p>
        </div>
      </div>

      {/* Live merch preview — appears when Merch tier is selected on the sign-up form */}
      {tier === "merch" && (
        <div data-testid="merch-preview-strip" className="mt-12 border-2 border-[#B32027]/40 bg-[#B32027]/5 p-5 md:p-6">
          <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
            <div>
              <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-1">/ Live drop</p>
              <p className="font-display font-black uppercase tracking-tighter text-xl">Pick your piece — we&rsquo;ll take you there next</p>
            </div>
            <Link
              to={shopHref()}
              data-testid="merch-preview-see-all"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/80 hover:text-[#B32027] transition-colors"
            >
              See all <ArrowUpRight size={11} weight="bold" />
            </Link>
          </div>
          {merchPreview && merchPreview.products && merchPreview.products.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1" style={{ scrollSnapType: "x mandatory" }}>
              {merchPreview.products.slice(0, 8).map((p) => (
                <Link
                  key={p.id}
                  to={shopHref()}
                  data-testid={`merch-preview-card-${p.id}`}
                  className="group flex-shrink-0 w-[220px] border border-white/15 bg-[#0F0F0F] hover:border-[#B32027] transition-colors"
                  style={{ scrollSnapAlign: "start" }}
                >
                  <div className="aspect-square bg-[#141414] overflow-hidden">
                    {p.thumbnail_url && (
                      <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm font-bold leading-tight line-clamp-2 group-hover:text-[#B32027] transition-colors">{p.name}</p>
                    {p.price_from != null && (
                      <p className="text-white/70 text-xs mt-1">
                        {p.price_to && p.price_to !== p.price_from
                          ? `$${Math.round(p.price_from)}–$${Math.round(p.price_to)}`
                          : `$${Math.round(p.price_from)}`}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-white/70 text-sm">
              Fresh drops are being loaded to the store. Prefer to give directly?{" "}
              <button type="button" onClick={() => setTier("custom")} data-testid="merch-preview-switch-donate" className="underline text-[#B32027] hover:text-[#D42730]">Switch to donate</button>.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
